import { useState } from 'react';

import { UnifiedTimeline } from '@/features/emr/components/UnifiedTimeline';
import type {
  EncounterOut,
} from '@/features/emr/api/encounters-api';
import type { GeneratedDocumentOut } from '@/features/emr/api/documents-api';
import type { UploadedDocumentOut } from '@/features/emr/types';

import {
  InlineDocumentViewer,
  type InlineDocumentItem,
} from './InlineDocumentViewer';
import { EncounterPreviewDialog } from './EncounterPreviewDialog';

interface TimelineTabProps {
  patientId: string;
}

function toItemFromGenerated(doc: GeneratedDocumentOut): InlineDocumentItem {
  return {
    id: doc.id,
    name: doc.template?.name ?? 'Generated Document',
    type: doc.status,
    date: doc.generated_at,
    content: doc.content,
    source: 'generated',
  };
}

function toItemFromUploaded(doc: UploadedDocumentOut): InlineDocumentItem {
  return {
    id: doc.id,
    name: doc.name,
    type: doc.document_type,
    date: doc.document_date,
    content: doc.current_revision?.extracted_text ?? null,
    source: 'uploaded',
  };
}

export function TimelineTab({ patientId }: TimelineTabProps) {
  const [selectedDoc, setSelectedDoc] = useState<InlineDocumentItem | null>(null);
  const [previewEncounter, setPreviewEncounter] = useState<EncounterOut | null>(
    null,
  );

  if (selectedDoc) {
    return (
      <InlineDocumentViewer
        item={selectedDoc}
        onBack={() => setSelectedDoc(null)}
      />
    );
  }

  return (
    <>
      <UnifiedTimeline
        patientId={patientId}
        onOpenGeneratedDocument={(doc) => setSelectedDoc(toItemFromGenerated(doc))}
        onOpenUploadedDocument={(doc) => setSelectedDoc(toItemFromUploaded(doc))}
        onOpenEncounter={(enc) => setPreviewEncounter(enc)}
        onUploadClick={() => {
          // Upload is not offered from the embedded reference panel.
        }}
        showUploadButton={false}
      />

      <EncounterPreviewDialog
        encounter={previewEncounter}
        onOpenChange={(open) => {
          if (!open) setPreviewEncounter(null);
        }}
      />
    </>
  );
}
